(ns app.front.ui.tabs
  (:require [app.front.ui.util :as uiu]
            [core.front.carbon :as cc]
            [potpuri.core :as p]
            [reagent.core :as r]))

(defn- visible-tabs [tabs]
  (->> tabs
       (remove :hidden?)
       vec))

(defn- selected-index [tabs selected-id]
  (or (first (keep-indexed (fn [idx {:keys [id]}]
                             (when (= id selected-id)
                               idx))
                           tabs))
      0))

(defn- selected-tab [tabs selected-id]
  (or (first (filter #(and (= selected-id (:id %))
                           (not (:disabled? %)))
                     tabs))
      (first (remove :disabled? tabs))
      (first tabs)))

(defn- tab-data-cy [data-cy id]
  (or data-cy (some->> id uiu/string-id (str "tab:"))))

(defn- tab-bar
  "Renders a tab bar without tab panels, for tabs that either navigate (`:href`) or only
   switch content rendered elsewhere on the page (`:on-change`). Carbon's Tab preventDefault
   breaks href navigation, so we render using Carbon's scrollable tab CSS classes directly.

   A tab that navigates is rendered as a link and one that only changes page content as a
   button, so that the browser affordances (opening in a new tab, keyboard activation) match
   what the tab actually does. The markup and ARIA are otherwise identical for both, which
   makes it safe to mix them. `role=\"tab\"` is deliberately not used: these tabs have no
   tab panel to control, so the selected tab is marked with `aria-current` instead."
  [{:keys [tabs selected-id on-change class tab-class aria-label]}]
  (let [tabs        (visible-tabs tabs)
        selected-id (or selected-id (-> tabs first :id))]
    [:div {:class class}
     [:div.bx--tabs--scrollable {:class tab-class}
      (into [:ul.bx--tabs--scrollable__nav {:aria-label aria-label}]
            (for [{:keys [id label href disabled? data-cy]} tabs
                  :let                                      [selected? (= id selected-id)]]
              [:li.bx--tabs--scrollable__nav-item
               {:class [(when selected? "bx--tabs--scrollable__nav-item--selected")
                        (when disabled? "bx--tabs__nav-item--disabled bx--tabs--scrollable__nav-item--disabled")]}
               (if href
                 [:a.bx--tabs--scrollable__nav-link
                  (p/assoc-if {:aria-current  (when selected? "page")
                               :tab-index     (if disabled? -1 0)
                               :aria-disabled (boolean disabled?)
                               :data-cy       (tab-data-cy data-cy id)}
                              :href (when-not disabled? href))
                  label]
                 [:button.bx--tabs--scrollable__nav-link
                  {:type         "button"
                   :aria-current (boolean selected?)
                   :disabled     (boolean disabled?)
                   :data-cy      (tab-data-cy data-cy id)
                   :on-click     #(when on-change
                                    (on-change id))}
                  label])]))]]))

(defn tabs
  "A small wrapper around Carbon tabs. Renders in one of two modes, depending on the tabs given:

  - *panel tabs* (any tab has `:content`): Carbon renders the tab bar and the active tab's
    `:content` below it.
  - *bar-only tabs* (no tab has `:content`): only the tab bar is rendered and the active tab's
    content is expected to be rendered elsewhere on the page. Tabs with an `:href` are links
    (use for switching between routes), tabs without are buttons calling `on-change`
    (use for switching content within a single route). The two can be mixed.

  Props:
  - `tabs`: vector of tab maps with `:id`, `:label`, and either `:content`, `:href` or neither.
    Optional keys: `:hidden?`, `:disabled?`, `:data-cy`.
  - `selected-id`: optional controlled selected tab id. Defaults to the first visible tab.
  - `on-change`: optional callback called with the selected tab id. Not called for `:href` tabs.
  - `class`, `tab-content-class`, `tab-class`: optional class names for the wrapper, content area, and tabs.
  - `aria-label`: optional accessible label for the tab list."
  [{:keys [tabs selected-id on-change class tab-content-class tab-class aria-label] :as props}]
  (if (not-any? :content (visible-tabs tabs))
    [tab-bar props]
    (r/with-let [*selected-id (r/atom nil)]
      (let [tabs                (visible-tabs tabs)
            selected-id         (or selected-id @*selected-id (-> tabs first :id))
            selected-tab        (selected-tab tabs selected-id)
            selected-tab-id     (:id selected-tab)
            on-selection-change (fn [idx]
                                  (when-let [tab-id (-> tabs (get idx) :id)]
                                    (reset! *selected-id tab-id)
                                    (when on-change
                                      (on-change tab-id))))]
        [:div {:class class}
         (when (seq tabs)
           (apply cc/tabs
                  (p/assoc-if {:selected               (selected-index tabs selected-tab-id)
                               :on-selection-change    on-selection-change
                               :tab-content-class-name (or tab-content-class "px-0 py-6")}
                              :class-name tab-class
                              :aria-label aria-label)
                  (for [{:keys [id label content href disabled? data-cy]} tabs]
                    (cc/tab (p/assoc-if {:key        id
                                         :label      label
                                         :disabled   (boolean disabled?)
                                         :class-name "app-ui-tabs__tab"}
                                        :href href
                                        :data-cy (tab-data-cy data-cy id))
                            content))))]))))
